import * as Yup from 'yup';
import { AnyObject } from 'yup/lib/types';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import {
  convertDateStringToISO,
  createDateSchema,
} from './CustomValidation/DateExtendedSchema';
import yup from './CustomValidation/yup-extended';
import { ExtendedDateSchemaType } from './ValidationTypes';

/** Cross-field term range tests; uses convertDateStringToISO like createDateSchema. */
export const boardMembersTermDateToMs = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value !== 'string') return null;
  const iso = convertDateStringToISO(value);
  if (typeof iso !== 'string' || !/^(\d{4})-(\d{2})-(\d{2})$/.test(iso)) {
    return null;
  }
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(y, m - 1, d).getTime();
  return Number.isNaN(t) ? null : t;
};

export const BoardMembersValidationSchema = yup.object().shape(
  {
    leaIdentifier: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .mustBeNumber()
      .min(4, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(4))
      .max(4, VALIDATION_MESSAGE.AT_MOST_CHARS(4))
      .notOneOf(['0000'], VALIDATION_MESSAGE.MUST_NOT_BE_EQUAL_TO('0000')),
    membershipType: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    positionType: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    firstName: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test('no-spaces-only', VALIDATION_MESSAGE.REQUIRED, (value) => {
        return !!(value && value.trim().length > 0 && value === value.trim());
      }),
    lastOrSurname: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test('no-spaces-only', VALIDATION_MESSAGE.REQUIRED, (value) => {
        return !!(value && value.trim().length > 0 && value === value.trim());
      }),
    email: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .email(VALIDATION_MESSAGE.MUST_BE_IN_VALID_EMAIL_FORMAT)
      .test('no-spaces-only', VALIDATION_MESSAGE.REQUIRED, (value) => {
        return !!(value && value.trim().length > 0 && value === value.trim());
      }),
    termStartDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test(
        'is-before-end',
        'Must be less than Term End Date',
        function (this: Yup.TestContext<AnyObject>, value: unknown) {
          const { termEndDate } = this.parent;
          const startMs = boardMembersTermDateToMs(value);
          const endMs = boardMembersTermDateToMs(termEndDate);
          if (startMs === null || endMs === null) return true;
          return startMs <= endMs;
        }
      ),
    termEndDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test(
        'is-after-start',
        'Must be greater than Term Start Date',
        function (this: Yup.TestContext<AnyObject>, value: unknown) {
          const { termStartDate } = this.parent;
          const startMs = boardMembersTermDateToMs(termStartDate);
          const endMs = boardMembersTermDateToMs(value);
          if (startMs === null || endMs === null) return true;
          return endMs >= startMs;
        }
      ),
    streetNumberAndName: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .max(150, VALIDATION_MESSAGE.AT_MOST_CHARS(150))
      .test('no-spaces-only', VALIDATION_MESSAGE.REQUIRED, (value) => {
        return !!(value && value.trim().length > 0 && value === value.trim());
      }),
    apartmentRoomOrSuiteNumber: yup
      .string()
      .max(60, VALIDATION_MESSAGE.AT_MOST_CHARS(60))
      .test(
        'no-leading-trailing-spaces-optional',
        'Must not have leading or trailing spaces',
        (value) => {
          return !value || value.trim() === value;
        }
      ),
    addressCity: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .max(30, VALIDATION_MESSAGE.AT_MOST_CHARS(30))
      .test('no-spaces-only', VALIDATION_MESSAGE.REQUIRED, (value) => {
        return !!(value && value.trim().length > 0 && value === value.trim());
      }),
    addressStateAbbreviation: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED),
    addressPostalCode: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .min(5, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(5))
      .max(17, VALIDATION_MESSAGE.AT_MOST_CHARS(17))
      .test('no-spaces-only', VALIDATION_MESSAGE.REQUIRED, (value) => {
        return !!(value && value.trim().length > 0 && value === value.trim());
      }),
  },
  [['termStartDate', 'termEndDate']]
);
